import { motion } from 'framer-motion'
import { ArrowUpDown, Briefcase, Plus, Archive, RotateCcw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { FilterBar, FilterSelect, SearchInput, SegmentedControl } from '../components/FilterBar'
import { PageHeader } from '../components/PageHeader'
import { ProgressBar } from '../components/ProgressBar'
import { StatusBadge } from '../components/StatusBadge'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { apiErrorMessage } from '../lib/api'
import { hasFullVisibility } from '../hooks/useScopedWork'
import { AddTeamMemberModal } from './team/AddTeamMemberModal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import type { TeamMember } from '../data/types'
import { cn } from '../lib/cn'

type SortKey = 'name' | 'workload' | 'performance'
const sortLabels: Record<SortKey, string> = { name: 'Name', workload: 'Workload', performance: 'Share posted' }
const nextSort: Record<SortKey, SortKey> = { name: 'workload', workload: 'performance', performance: 'name' }

export default function Team() {
  const { team, archivedTeam, reload, archivePerson, restorePerson } = useAppData()
  const { showToast } = useToast()
  const [tab, setTab] = useState('team')
  // Archiving signs somebody out of Hapi, so it asks first.
  const [archiving, setArchiving] = useState<TeamMember | null>(null)
  const { currentUser, hasRole } = useAuth()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [department, setDepartment] = useState('All departments')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  // The departments people are actually in, not a list of the ones a studio
  // might have: a filter option that matches nobody is a dead end.
  const departments = useMemo(
    () => ['All departments', ...Array.from(new Set([...team, ...archivedTeam].map((m) => m.department))).sort()],
    [team, archivedTeam],
  )

  // Team Members/Viewers only get a directory of one: their own linked
  // persona's card, not everyone else's task counts and performance scores.
  const visibleTeam = hasFullVisibility(currentUser?.role)
    ? team
    : team.filter((m) => m.id === currentUser?.linkedPersonaId)

  const visibleArchived = hasFullVisibility(currentUser?.role)
    ? archivedTeam
    : archivedTeam.filter((m) => m.id === currentUser?.linkedPersonaId)

  const currentList = tab === 'team' ? visibleTeam : visibleArchived

  const filtered = useMemo(() => {
    const list = currentList.filter((m) => {
      if (search && !m.name.toLowerCase().includes(search.toLowerCase()) && !m.title.toLowerCase().includes(search.toLowerCase())) return false
      if (department !== 'All departments' && m.department !== department) return false
      return true
    })
    return [...list].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name)
      if (sortKey === 'workload') return b.workload - a.workload
      return b.performance - a.performance
    })
  }, [currentList, search, department, sortKey])

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Team"
        description="Who is here, and who has room."
        actions={
          hasRole('PARTNER') && (
            <Button onClick={() => setInviteOpen(true)}>
              <Plus size={16} /> Add somebody
            </Button>
          )
        }
      />

      <div>
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={[
            { label: `Team · ${visibleTeam.length}`, value: 'team' },
            { label: `Archived · ${visibleArchived.length}`, value: 'archived' }
          ]}
        />
      </div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name or role…" className="w-full sm:w-64" />
        <FilterSelect
          label="Filter by department"
          value={department}
          onChange={setDepartment}
          options={departments.map((d) => ({ label: d, value: d }))}
        />
        <button
          onClick={() => setSortKey(nextSort[sortKey])}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-shell-border bg-shell px-3 text-sm text-shell-text hover:bg-shell-border/40"
        >
          <ArrowUpDown size={13} />
          Sort: {sortLabels[sortKey]}
        </button>
      </FilterBar>

      {filtered.length === 0 ? (
        <EmptyState title="Nobody here" description="Try a different search, or add somebody." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -40px 0px' }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
              whileHover={{ y: -3 }}
            >
              <div className={cn("flex flex-col rounded-card border border-shell-border bg-shell shadow-card transition-shadow hover:shadow-card-hover overflow-hidden", tab === 'archived' && "opacity-60 grayscale")}>
              <Link
                to={`/people/${m.id}`}
                className="flex flex-col gap-4 p-5 flex-1"
>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar name={m.name} color={m.color} size="lg" />
                    <div>
                      <p className="font-display text-sm font-semibold text-shell-text-active">{m.name}</p>
                      <p className="text-xs text-shell-text">{m.title}</p>
                    </div>
                  </div>
                  <StatusBadge status={m.status} />
                </div>

                <div className="flex items-center gap-1.5 text-xs text-shell-text">
                  <Briefcase size={12} />
                  {m.department} &middot;{' '}
                  {m.clients === 1 ? '1 client' : `${m.clients} clients`}
                </div>

                <div className="grid grid-cols-4 gap-2 rounded-xl bg-shell-border/40 p-2.5 text-center">
                  <div>
                    <p className="font-display text-base font-bold text-shell-text-active">{m.activeTasks}</p>
                    <p className="text-[10px] font-medium text-shell-text">Open</p>
                  </div>
                  <div>
                    <p className="font-display text-base font-bold text-shell-text-active">{m.completedTasks}</p>
                    <p className="text-[10px] font-medium text-shell-text">Went out</p>
                  </div>
                  <div>
                    {/*
                      * Amber, not red, and only on the count itself. Being on
                      * eleven accounts is not a mistake somebody made — it is
                      * usually the studio's own doing — and the thing that
                      * fails first is not the work but the following-up. The
                      * title says that rather than leaving a coloured number
                      * to be guessed at.
                      */}
                    <p
                      className={cn(
                        'font-display text-base font-bold',
                        m.tooManyClients ? 'text-warning-500' : 'text-shell-text-active',
                      )}
                      title={
                        m.tooManyClients
                          ? `${m.name.split(' ')[0]} is on ${m.clients} accounts. Past about ten, chasing approvals is what slips first.`
                          : undefined
                      }
                    >
                      {m.clients}
                    </p>
                    <p className="text-[10px] font-medium text-shell-text">Clients</p>
                  </div>
                  <div>
                    {/*
                      * Not red, amber and green. The design graded this cell
                      * like an exam mark, and the number it grades goes down
                      * when somebody is given more work — so the person who
                      * has just been handed a week of briefs turns red for it.
                      */}
                    <p className="font-display text-base font-bold text-shell-text-active">{m.performance}%</p>
                    <p className="text-[10px] font-medium text-shell-text">Posted</p>
</div>
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-shell-text">Workload</span>
                    <span className="font-medium text-shell-text-active">{m.workload}%</span>
                  </div>
                  <ProgressBar value={m.workload} color={m.workload > 85 ? '#e0433a' : m.color} height={5} trackClassName="bg-shell-border" />
                </div>
              </Link>

                {/* Not on your own card: archiving yourself signs you out for good. */}
                {hasRole('PARTNER') && m.id !== currentUser?.linkedPersonaId && (
                  <div className="border-t border-shell-border px-5 py-3 bg-shell-border/20 flex justify-end">
                    {tab === 'team' ? (
                      <Button variant="secondary" size="sm" onClick={() => setArchiving(m)}>
                        <Archive size={14} className="mr-1.5" /> Archive
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          try {
                            await restorePerson(m.id)
                            showToast(`${m.name} is back on the team.`)
                          } catch (err) {
                            showToast(apiErrorMessage(err), 'error')
                          }
                        }}
                      >
                        <RotateCcw size={14} className="mr-1.5" /> Bring back
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={archiving !== null}
        onClose={() => setArchiving(null)}
        danger
        title={`Archive ${archiving?.name ?? ''}?`}
        description="They will not be able to sign in, and they drop out of every picker. Their past work keeps their name. Bring them back from the Archived tab at any time."
        confirmLabel="Archive"
        onConfirm={() => {
          const person = archiving
          if (person === null) return
          void archivePerson(person.id).then(
            () => showToast(`${person.name} moved to the archive.`),
            (err: unknown) => showToast(apiErrorMessage(err), 'error'),
          )
        }}
      />
      <AddTeamMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={reload}
      />
    </div>
  )
}
